/**
 * Blackball, as a pure state machine.
 *
 * `applyShot(frame, shot)` returns a NEW frame. No mutation, no I/O, no clock —
 * so every rule below is testable by calling a function, and the screen is only
 * ever a rendering of what it returns.
 *
 * It lives in the browser rather than on the server because a frame has to be
 * scorable in a cellar with no signal, and a round trip per shot is not that.
 * Shots are posted as they happen for durability; the frame's OUTCOME is still
 * reported as a FrameWon, which is what the rating replays. The server does not
 * referee — this is an app for friends, and a wrong call is settled by the two
 * people standing at the table, not by a validator.
 *
 * Written against the WPA Blackball rules (859562690-blackballrules.pdf); rule
 * numbers below refer to it. Where the mockup and the rules disagreed the rules
 * won, except where a rule cannot be judged without a referee — a legal shot
 * requires a cushion after contact (5d), a snooker escape does not (5g), and
 * neither is visible to an app. Those are what the FOUL button is for: the two
 * people at the table are the referee, and this only has to keep the score.
 *
 * The foul penalty is 6a: the offender loses their next visit and the opponent
 * gets A FREE SHOT PLUS ONE VISIT. The free shot is its own thing (6b) — any
 * ball may be hit and potted without penalty, and the table stays open through
 * it (4e c) — and it is the first shot only. Modelling it as plain "two visits"
 * got three separate things wrong, which is why it is a flag and not a count.
 */

/**
 * THE TWO RULESETS THIS APP KNOWS, and the single thing they disagree about.
 *
 * They differ on one question: after a foul, when the incoming player POTS on
 * their free shot, is the owed second visit spent or kept?
 *
 *   EPA Blackball    kept. This is the published rule and it is unambiguous.
 *                    EPA 6(A): a foul gives "two consecutive visits". 6(C):
 *                    on the free shot, if a ball is pocketed "the player is
 *                    deemed to have pocketed a legal ball(s), AND CONTINUES
 *                    WITH THE FIRST VISIT... When the player fails to pot a
 *                    ball on the first or subsequent shot of the first visit,
 *                    play then continues with the second visit." So potting
 *                    never costs you the second visit; only missing does.
 *
 *   BDIPL Blackball  spent. The local variant, and the DEFAULT here, because
 *                    this instance serves BDIPL. Taking the free shot and
 *                    making it count is the reward; the spare visit is what
 *                    you get INSTEAD, when the free shot puts nothing down.
 *
 * This is a house rule, recorded as one. It is not a correction of 6(C) and
 * nothing here should be read as claiming the published rule says this —
 * that was checked against the EPA text before the variant was written, and
 * the published rule says the opposite.
 *
 * Nothing else differs. A ruleset is a flag on the frame rather than a
 * global, so a frame always replays under the rules it was played under.
 */
export const RULESETS = {
  bdipl: { id: 'bdipl', label: 'BDIPL Blackball', potSpendsOwedVisit: true },
  epa: { id: 'epa', label: 'EPA Blackball', potSpendsOwedVisit: false },
};

export const DEFAULT_RULESET = 'bdipl';

/** Tolerant of an absent or unknown id: a frame recorded before rulesets
 *  existed, or by a newer client, still has to score. */
export const rulesOf = (frame) =>
  RULESETS[(frame && frame.ruleset)] || RULESETS[DEFAULT_RULESET];

export const RED = 'red';
export const YELLOW = 'yellow';
export const BLACK = 'black';

/**
 * The shot vocabulary.
 *
 * Some of these are the same rule under two names, on purpose. A safety and a
 * missed pot both end the visit and the rules do not tell them apart — but the
 * players do, and a log that calls a deliberate safety "nothing down" is
 * describing a different game. The rule is one thing; what happened is another.
 *
 * `safe` is the id the first version wrote, and shots already on the log carry
 * it. An append-only log keeps every shape it has written, so `safe` is read
 * for ever even though nothing writes it now.
 */
const PASSES = new Set(['safe', 'miss', 'safety']);
const FOULS = new Set(['foul', 'whitedown']);

export const BREAK_SHOTS = [
  { id: 'pot', label: 'Balls down', icon: 'circle-dot' },
  // The mockup has no way to say this, because its "balls down" is disabled
  // until a ball is chosen — so the commonest break of all, the one where
  // nothing drops and nothing fouls, could not be recorded at all.
  { id: 'miss', label: 'Nothing down', icon: 'x', tone: 'muted' },
  { id: 'whitedown', label: 'White down', icon: 'alert-triangle', tone: 'danger' },
  { id: 'foul', label: 'Illegal break', icon: 'alert-triangle', tone: 'danger' },
  { id: 'rerack', label: 'Re-rack', icon: 'rotate-ccw', tone: 'muted' },
];

/** 4b(b): the opponent may call a re-rack after an illegal break — and break. */
export const TAKE_BREAK = {
  id: 'takebreak', label: 'Re-rack, my break', icon: 'rotate-ccw', tone: 'brass',
};

export const PLAY_SHOTS = [
  { id: 'pot', label: 'Balls down', icon: 'circle-dot' },
  { id: 'foul', label: 'Foul', icon: 'alert-triangle', tone: 'danger' },
  { id: 'miss', label: 'No balls down', icon: 'x', tone: 'muted' },
  { id: 'safety', label: 'Safety', icon: 'shield', tone: 'brass' },
  { id: 'rerack', label: 'Re-rack', icon: 'rotate-ccw', tone: 'muted' },
  // "Conceded", not "Defaulted": this is a player giving up a frame they
  // are IN, which is a PLAYED frame and moves their rating. A league
  // walkover is nobody turning up at all and counts for the table only.
  // Two meanings of one word, one screen apart, is a bug waiting to happen.
  // The id stays `concede` because the log already carries it.
  { id: 'concede', label: 'Conceded', icon: 'ban', tone: 'danger' },
];

/** A frame at the moment of the break. `breaker` is a player id for a casual
 * match — but leagues.js's `replay()` calls this with SIDES ('home'/'away')
 * instead, because a league frame can be doubles, and "a player id" cannot
 * name two people. Everything below keys off whatever `breaker`/`opponent`
 * actually are, so a side works exactly as a player id would; nothing here
 * has to know which one it was handed. */
export function newFrame(breaker, opponent, { ruleset = DEFAULT_RULESET } = {}) {
  return {
    breaker,
    ruleset,
    players: [breaker, opponent],
    turn: breaker,
    stage: 'break',
    groups: {},               // player id (or league SIDE) -> RED | YELLOW, once decided
    remaining: { [RED]: 7, [YELLOW]: 7 },
    // Rule 6a: a foul costs the offender their next visit and gives the
    // opponent "a free shot plus one visit" — so two, the first of which is
    // free. `freeShot` is the first shot only (6b) and changes what is legal.
    visitsLeft: 1,
    freeShot: false,
    // Rule 4b(b): an illegal break lets the opponent take the free shot OR
    // call a re-rack — and if they call it, they break. True only until they
    // decide, which is their first shot.
    breakFoul: false,
    // Which visit we are in, and what the player at the table has potted
    // during it. A clearance is a property of a VISIT, not of a frame, so it
    // cannot be worked out from the final score alone.
    visit: 1,
    visitPots: { [RED]: 0, [YELLOW]: 0 },
    over: false,
    winner: null,
    reason: null,
    history: [],
  };
}

const other = (frame, player) => frame.players.find((p) => p !== player);

function finish(frame, winner, reason) {
  return { ...frame, over: true, winner, reason, turn: null };
}

/** End of a visit: hand over, unless visits are owed from a foul. */
function passTurn(frame) {
  const fresh = { visit: frame.visit + 1, freeShot: false,
                  visitPots: { [RED]: 0, [YELLOW]: 0 } };
  if (frame.visitsLeft > 1) {
    return { ...frame, ...fresh, visitsLeft: frame.visitsLeft - 1 };
  }
  return { ...frame, ...fresh, turn: other(frame, frame.turn), visitsLeft: 1 };
}

/** Rule 6a: a free shot plus one visit for the player coming to the table. */
function foul(frame, { onBreak = false } = {}) {
  return { ...frame, turn: other(frame, frame.turn), visitsLeft: 2,
           freeShot: true, breakFoul: onBreak,
           visit: frame.visit + 1,
           visitPots: { [RED]: 0, [YELLOW]: 0 } };
}

/** What the player at the table may record right now. */
export function shotsFor(frame) {
  if (!frame) return PLAY_SHOTS;
  if (frame.stage === 'break') return BREAK_SHOTS;
  // Offered only to the player an illegal break just handed the table to,
  // and only until they play — because taking the break is one of the three
  // things 4b(b) lets them choose between, not a standing option.
  return frame.breakFoul ? [...PLAY_SHOTS, TAKE_BREAK] : PLAY_SHOTS;
}

function assignGroups(frame, potted) {
  const colours = [RED, YELLOW].filter((c) => potted[c] > 0);
  // Potting both colours in one shot leaves the table open: nobody has shown
  // which group they are on.
  if (colours.length !== 1) return frame.groups;
  const mine = colours[0];
  return {
    ...frame.groups,
    [frame.turn]: mine,
    [other(frame, frame.turn)]: mine === RED ? YELLOW : RED,
  };
}

const tableOpen = (frame) => Object.keys(frame.groups).length === 0;

function clearedOwnGroup(frame, remaining, player) {
  const group = frame.groups[player];
  return group ? remaining[group] === 0 : false;
}

/**
 * @param shot {id: 'pot'|'safe'|'foul'|'rerack'|'concede',
 *              potted?: {red, yellow, black}}
 */
export function applyShot(frame, shot) {
  if (frame.over) return frame;
  const at = frame.turn;
  const note = (next, text) => ({
    ...next,
    history: [...frame.history,
              { player: at, text, group: (next.groups || {})[at] || null }],
  });

  if (shot.id === 'rerack') {
    // Same breaker, clean table, no penalty (4d for the black on the break,
    // 4r for a stalemate).
    return note({ ...newFrame(frame.breaker, other(frame, frame.breaker),
                            { ruleset: frame.ruleset }),
                  history: frame.history }, 'Re-rack');
  }

  if (shot.id === 'takebreak') {
    // 4b(b): after an illegal break the opponent may call a re-rack instead of
    // taking the free shot — and it becomes their break. Ignored otherwise,
    // because it is a choice that only exists in that one moment.
    if (!frame.breakFoul) return frame;
    return note({ ...newFrame(at, other(frame, at), { ruleset: frame.ruleset }),
                 history: frame.history },
                'Re-rack — their break');
  }

  if (shot.id === 'concede') {
    return note(finish(frame, other(frame, at), 'Conceded'), 'Conceded');
  }

  // Any shot ends the break, whatever it was. Missing this meant the shot
  // AFTER a missed break was still judged as a break — and groups are never
  // decided on a break, so nobody was ever on a colour.
  const onBreak = frame.stage === 'break';
  const played = { ...frame, stage: 'play', breakFoul: false };

  if (PASSES.has(shot.id)) {
    // Identical in the rules, different in the record: see PASSES above.
    return note(passTurn(played),
                shot.id === 'safety' ? 'Safety' : 'Nothing down');
  }

  // What went down, and what that leaves. A foul can put balls down too — the
  // penalty is about the offence, not about the table staying as it was.
  const potted = { [RED]: 0, [YELLOW]: 0, black: false, ...(shot.potted || {}) };
  const remaining = {
    [RED]: Math.max(0, frame.remaining[RED] - potted[RED]),
    [YELLOW]: Math.max(0, frame.remaining[YELLOW] - potted[YELLOW]),
  };

  if (FOULS.has(shot.id)) {
    // Rule 7a: a foul in a shot in which the black is potted is loss of frame,
    // whatever else was true — including having cleared your own group. It is
    // the one place a foul does more than hand over the table.
    if (potted.black) {
      return note(finish({ ...played, remaining }, other(frame, at),
                         'Foul on the black'),
                  'Black potted on a foul — frame lost');
    }
    // Rule 4f: balls potted on a foul are IGNORED in deciding groups — but
    // they have still left the table, and whether the black is legal later
    // depends on what is actually on it. So the count moves and the groups
    // do not.
    const down = potted[RED] + potted[YELLOW] > 0;
    const what = shot.id === 'whitedown' ? 'White down' : 'Foul';
    return note(foul({ ...played, remaining }, { onBreak }),
                down ? `${describe(potted)} — ${what.toLowerCase()}, free shot`
                     : `${what} — free shot`);
  }

  if (frame.stage === 'break') {
    if (potted.black) {
      // Black on the break is a re-rack, not a win and not a loss.
      return note({ ...newFrame(frame.breaker, other(frame, frame.breaker),
                                { ruleset: frame.ruleset }),
                    history: frame.history }, 'Black on the break — re-rack');
    }
    // Rule 4e(a): groups are never decided on the break, whatever went down.
    // Rule 4g: but potting IS a legal shot, so the breaker stays at the table
    // and plays on with the table still open. Both branches here used to pass
    // the turn — written as a conditional whose arms were identical, which is
    // the shape a rule looks like when it was never actually applied.
    const potsMade = potted[RED] + potted[YELLOW] > 0;
    const next = { ...frame, remaining, stage: 'play', freeShot: false,
                   breakFoul: false };
    return note(potsMade ? next : passTurn(next),
                potsMade ? describe(potted) : 'Nothing down');
  }

  if (potted.black) {
    const groups = frame.groups;
    if (clearedOwnGroup({ ...frame, groups }, remaining, at)) {
      // All seven of their own group in this one visit, then the black.
      const mine = groups[at];
      const dished = frame.visitPots[mine] === 7;
      const won = finish({ ...frame, remaining }, at, 'Black potted');
      return note({
        ...won,
        clearance: dished
          ? (frame.visit === 1 && at === frame.breaker ? 'break_and_dish' : 'dish')
          : null,
      }, dished ? 'Black — cleared in one visit' : 'Black — frame won');
    }
    return note(finish({ ...frame, remaining }, other(frame, at),
                       'Black potted early'),
                'Black potted early — frame lost');
  }

  // Rule 4e: groups are NOT decided and the table stays open on a break shot,
  // on a foul shot, on a FREE shot after a foul, or on a combination potting
  // both colours. So a free shot can put balls down and leave the table open.
  const groups = (tableOpen(frame) && !frame.freeShot)
    ? assignGroups({ ...frame, remaining }, potted)
    : frame.groups;
  const mine = groups[at];
  // Rule 5c: potting an opponent's ball without one of your own is a foul,
  // "except when the table is open" — and rule 6b makes a free shot exactly
  // that: any ball may be played and potted without penalty, once.
  const pottedOnlyTheirs = mine && !frame.freeShot
    && potted[mine] === 0 && potted[mine === RED ? YELLOW : RED] > 0;

  // The one place the two rulesets differ — see RULESETS at the top.
  //
  // Only when the pot happened ON the free shot: `freeShot` and
  // `visitsLeft: 2` are set together by `foul()` and by nothing else, so this
  // can never fire on an ordinary pot in an ordinary visit.
  const spent = (rulesOf(frame).potSpendsOwedVisit && frame.freeShot)
    ? Math.max(1, frame.visitsLeft - 1)
    : frame.visitsLeft;
  const next = { ...frame, remaining, groups, stage: 'play', freeShot: false,
                 visitsLeft: spent, breakFoul: false };
  if (pottedOnlyTheirs) {
    return note(foul(next), `${describe(potted)} — foul`);
  }
  // Rule 4g: potting legally entitles you to another shot.
  return note({ ...next, visitPots: {
    [RED]: frame.visitPots[RED] + potted[RED],
    [YELLOW]: frame.visitPots[YELLOW] + potted[YELLOW],
  } }, describe(potted));
}

export function describe(potted) {
  const parts = [];
  if (potted[RED]) parts.push(`${potted[RED]} red${potted[RED] > 1 ? 's' : ''}`);
  if (potted[YELLOW]) {
    parts.push(`${potted[YELLOW]} yellow${potted[YELLOW] > 1 ? 's' : ''}`);
  }
  if (potted.black) parts.push('black');
  return parts.length ? `${parts.join(' and ')} down` : 'Nothing down';
}

/**
 * How the frame was won, when it was won in one visit.
 *
 * 'break_and_dish' is from the break; 'dish' is the same clearance arrived at
 * later in the frame. Null for an ordinary win, and null for a concession —
 * neither is a clearance, and calling it one would flatter somebody.
 */
export function clearanceOf(frame) {
  return frame.clearance || null;
}

/** What the player at the table may still pot, for the shot pad. */
export function onGroup(frame) {
  return frame.groups[frame.turn] || null;
}
